import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import type { Goal, SubTask, Task } from '../types/timeline'
import { DataContext, type DataContextValue } from './dataContextCore'
import { useAuth } from './useAuth'
import { isNoSignInPreviewActive } from '../debug'
import { loadUserGoals, saveUserGoals } from '../services/timelineStore'
import { isHelperOnlyAccount } from '../utils/helperMembership'

const mergeGoals = (localGoals: Goal[], cloudGoals: Goal[]) => {
  const merged = new Map<string, Goal>()
  cloudGoals.forEach((goal) => merged.set(goal.id, goal))
  localGoals.forEach((goal) => merged.set(goal.id, goal))
  return Array.from(merged.values())
}

export const DataProvider = ({ children }: PropsWithChildren) => {
  const { user, userProfile } = useAuth()
  const [goals, setGoals] = useState<Goal[]>([])
  const [hasUnsavedAnonymousTimeline, setHasUnsavedAnonymousTimeline] = useState(false)
  const userIdRef = useRef<string | null>(null)

  const persistGoals = useCallback(
    async (nextGoals: Goal[]) => {
      if (!userIdRef.current) return
      if (isHelperOnlyAccount({ userProfile, currentUser: user })) return
      if (isNoSignInPreviewActive()) return
      await saveUserGoals(userIdRef.current, nextGoals)
    },
    [user, userProfile],
  )

  useEffect(() => {
    userIdRef.current = user?.uid ?? null

    if (!user) return
    if (isHelperOnlyAccount({ userProfile, currentUser: user })) return
    if (isNoSignInPreviewActive()) return

    let isCancelled = false

    void loadUserGoals(user.uid)
      .then((cloudGoals) => {
        if (isCancelled) return

        setGoals((currentGoals) => {
          const mergedGoals = mergeGoals(currentGoals, cloudGoals)
          void saveUserGoals(user.uid, mergedGoals)
          return mergedGoals
        })
        setHasUnsavedAnonymousTimeline(false)
      })
      .catch((error) => {
        console.warn('[DataProvider] loadUserGoals failed', error)
      })

    return () => {
      isCancelled = true
    }
  }, [user, userProfile?.role])

  const addGoal = useCallback((newGoal: Goal) => {
    setGoals((currentGoals) => {
      const nextGoals = [...currentGoals, newGoal]
      if (!userIdRef.current && currentGoals.length === 0) setHasUnsavedAnonymousTimeline(true)
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const updateGoal = useCallback((goalId: string, updatedGoal: Partial<Goal>) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) => (goal.id === goalId ? { ...goal, ...updatedGoal } : goal))
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const deleteGoal = useCallback((goalId: string) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.filter((goal) => goal.id !== goalId)
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const addTask = useCallback((goalId: string, newTask: Task) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) =>
        goal.id === goalId ? { ...goal, tasks: [...goal.tasks, newTask] } : goal,
      )
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const updateTask = useCallback((goalId: string, taskId: string, updatedTask: Partial<Task>) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) =>
        goal.id === goalId
          ? {
              ...goal,
              tasks: goal.tasks.map((task) =>
                task.id === taskId ? { ...task, ...updatedTask } : task,
              ),
            }
          : goal,
      )
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const deleteTask = useCallback((goalId: string, taskId: string) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) =>
        goal.id === goalId
          ? { ...goal, tasks: goal.tasks.filter((task) => task.id !== taskId) }
          : goal,
      )
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const addSubTask = useCallback((goalId: string, taskId: string, newSubTask: SubTask) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) =>
        goal.id === goalId
          ? {
              ...goal,
              tasks: goal.tasks.map((task) =>
                task.id === taskId
                  ? { ...task, subTasks: [...task.subTasks, newSubTask] }
                  : task,
              ),
            }
          : goal,
      )
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const updateSubTask = useCallback(
    (goalId: string, taskId: string, subTaskId: string, updatedSubTask: Partial<SubTask>) => {
      setGoals((currentGoals) => {
        const nextGoals = currentGoals.map((goal) =>
          goal.id === goalId
            ? {
                ...goal,
                tasks: goal.tasks.map((task) =>
                  task.id === taskId
                    ? {
                        ...task,
                        subTasks: task.subTasks.map((subTask) =>
                          subTask.id === subTaskId
                            ? { ...subTask, ...updatedSubTask }
                            : subTask,
                        ),
                      }
                    : task,
                ),
              }
            : goal,
        )
        void persistGoals(nextGoals)
        return nextGoals
      })
    },
    [persistGoals],
  )

  const deleteSubTask = useCallback((goalId: string, taskId: string, subTaskId: string) => {
    setGoals((currentGoals) => {
      const nextGoals = currentGoals.map((goal) =>
        goal.id === goalId
          ? {
              ...goal,
              tasks: goal.tasks.map((task) =>
                task.id === taskId
                  ? {
                      ...task,
                      subTasks: task.subTasks.filter((subTask) => subTask.id !== subTaskId),
                    }
                  : task,
              ),
            }
          : goal,
      )
      void persistGoals(nextGoals)
      return nextGoals
    })
  }, [persistGoals])

  const dismissAnonymousTimelineWarning = useCallback(() => {
    setHasUnsavedAnonymousTimeline(false)
  }, [])

  const value = useMemo<DataContextValue>(
    () => ({
      goals,
      hasUnsavedAnonymousTimeline,
      dismissAnonymousTimelineWarning,
      addGoal,
      updateGoal,
      deleteGoal,
      addTask,
      updateTask,
      deleteTask,
      addSubTask,
      updateSubTask,
      deleteSubTask,
    }),
    [
      addGoal,
      addSubTask,
      addTask,
      deleteGoal,
      deleteSubTask,
      deleteTask,
      dismissAnonymousTimelineWarning,
      goals,
      hasUnsavedAnonymousTimeline,
      updateGoal,
      updateSubTask,
      updateTask,
    ],
  )

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}
